"use client"

import { Mail, Send } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import type { ReactNode } from "react"
import type { NotificationChannelType } from "@/lib/notifications/types"
import { useTranslations } from "next-intl"



export interface Destination {
  id: string
  type: NotificationChannelType
  name: string
  /** Already summarized — this component reads no channel `config`. */
  recipientsShort: string
  recipientsFull: string
  /** Why it would be skipped, in the dispatcher's terms; `null` = it will deliver. */
  warning: string | null
  /** e.g. "default" / "used instead" when the system channel takes over. */
  note?: string
}

interface DestinationListProps {
  destinations: Destination[]
  /** Rendered instead of the list when there is no usable destination at all. */
  emptyFallbackNote?: ReactNode
}

/**
 * Where an alert actually lands. The dispatcher drops undeliverable channels and falls back
 * to the system one, so a list that only showed the selection would name destinations the
 * alert never reaches.
 */
export function DestinationList({ destinations, emptyFallbackNote }: DestinationListProps) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsDestinationList")

  return (
    <div className="rounded-md border bg-muted/40 px-3 py-2">
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span key={tUi("alertsAreDeliveredTo")}>{tUi("alertsAreDeliveredTo")}</span></p>

      {destinations.length > 0 && (
        <ul className="space-y-1">
          {destinations.map((destination) => (
            <DestinationRow key={destination.id} destination={destination} />
          ))}
        </ul>
      )}

      {emptyFallbackNote && <p className="text-xs text-destructive">{emptyFallbackNote}</p>}
    </div>
  )
}

function DestinationRow({ destination }: { destination: Destination }) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsDestinationList")

  const { type, name, recipientsShort, recipientsFull, warning, note } = destination

  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {type === "email" ? (
        <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <Send className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      )}
      <span className={`font-medium ${warning ? "text-destructive line-through" : ""}`}>{name}</span>
      {note && (
        <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
          {note}
        </Badge>
      )}
      <span className="min-w-0 truncate text-muted-foreground" title={recipientsFull}>
        {recipientsShort}
      </span>
      {warning && <span className="text-destructive">{<span key={tUi("value0SoItIsSkipped", { value0: String(warning) })}>{tUi("value0SoItIsSkipped", { value0: String(warning) })}</span>}</span>}
    </li>
  )
}
